import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Paginator } from "primereact/paginator";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import AuditActionLink from "../../components/audit/AuditActionLink";
import AuditHistoriesLink from "../../components/audit/AuditHistoriesLink";
import type { SnackbarData } from "../../components/Snackbar";
import EmptyModal from "../../components/modals/EmptyModal";
import OperationalDataTableSkeleton from "../../components/operational/OperationalDataTableSkeleton";
import OperationalTableToolbar from "../../components/operational/OperationalTableToolbar";
import { withRouter } from "../../shared/utility";
import * as actions from "../../store/actions/index";
import { RecipesViewModel } from "../../viewmodels/recipes/RecipesViewModel";

interface RecipesProps {
  addSnackbar: (snackbar: SnackbarData) => void;
  logout: () => void;
}

interface PageChangeEvent {
  first: number;
  rows: number;
  page: number;
}

function Recipes({ addSnackbar, logout }: RecipesProps) {
  const {
    askDeleteRecipe,
    emptyModal,
    getRecipes,
    loading,
    recipes,
    setEmptyModelOpen,
  } = RecipesViewModel(logout, addSnackbar);

  const searchRef = useRef<HTMLInputElement>(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);

  useEffect(() => {
    void getRecipes(null, 1);
    // getRecipes is owned by this screen and intentionally runs on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setFirst(0);
    void getRecipes(searchRef.current?.value ?? "", 1);
  };

  const handlePagination = (event: PageChangeEvent) => {
    const nextPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    void getRecipes(searchRef.current?.value ?? "", nextPage);
  };

  return (
    <div>
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="card-custom overflow-hidden">
            <div className="px-4 py-5 sm:px-5">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                Recipes
              </h1>
            </div>
            <OperationalTableToolbar
              className=""
              filterActions={null}
              footer={null}
              searchRowClassName="sm:max-w-md"
              searchRow={
                <div className="flex">
                  <input
                    type="search"
                    name="searchRecipe"
                    id="searchRecipe"
                    className="block w-full rounded-l-md border border-gray-300 pl-2 sm:text-sm"
                    placeholder="Search by name"
                    ref={searchRef}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") handleSearch();
                    }}
                  />
                  <button
                    type="button"
                    className="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1"
                    onClick={handleSearch}
                  >
                    <MagnifyingGlassIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              }
              trailing={
                <>
                  <Link
                    className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    to="/create-recipe"
                  >
                    Add Recipe
                  </Link>
                  <AuditHistoriesLink slug="recipe" />
                </>
              }
            />
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                {loading ? (
                  <OperationalDataTableSkeleton
                    columns={["Name", "Number", "Actions"]}
                    rows={8}
                  />
                ) : (
                  <div className="card-custom overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-white">
                        <tr>
                          <th
                            scope="col"
                            className="py-3 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                          >
                            Name
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left text-sm font-semibold text-gray-900"
                          >
                            Number
                          </th>
                          <th
                            scope="col"
                            className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                          ></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {recipes.data.length > 0 ? (
                          recipes.data.map((recipe) => (
                            <tr key={recipe.id}>
                              <td className="whitespace-nowrap py-1.5 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                {recipe.recipe_name}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                                {recipe.recipe_number}
                              </td>
                              <td className="relative py-1.5 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <div className="flex flex-wrap items-center justify-end gap-2">
                                  <Link
                                    className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    to={`/recipes/${recipe.id}`}
                                  >
                                    Edit
                                  </Link>
                                  <AuditActionLink
                                    slug="recipe"
                                    id={recipe.id}
                                    name={recipe.recipe_name}
                                  />
                                  <button
                                    onClick={() => askDeleteRecipe(recipe)}
                                    className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              className="whitespace-nowrap py-3 pl-4 pr-3 text-center text-sm font-medium text-gray-900 sm:pl-6"
                              colSpan={3}
                            >
                              No Recipes
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="w-full">
                  <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={recipes.total ?? 0}
                    onPageChange={handlePagination}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapDispatchToProps = (dispatch: any) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar: SnackbarData) =>
    dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(connect(null, mapDispatchToProps)(Recipes));
